import { useState, useEffect, RefObject } from 'react';

interface Dimensions {
  width: number;
  height: number;
}

interface UseElementDimensionsOptions {
  defaultWidth?: number;
  defaultHeight?: number;
  widthOffset?: number;
  heightOffset?: number;
}

/**
 * Custom hook that tracks the dimensions of an HTML element
 * @param ref - React ref to the element to track
 * @param providedWidth - Optional fixed width (if provided, skips measurement)
 * @param providedHeight - Optional fixed height (if provided, skips measurement)
 * @param options - Configuration options for dimension calculation
 * @returns Current dimensions of the element
 */
export function useElementDimensions(
  ref: RefObject<HTMLElement>,
  providedWidth?: number,
  providedHeight?: number,
  options: UseElementDimensionsOptions = {}
): Dimensions {
  const {
    defaultWidth = 600,
    defaultHeight = 200,
    widthOffset = 0,
    heightOffset = 0
  } = options;

  const [dimensions, setDimensions] = useState<Dimensions>({
    width: providedWidth || defaultWidth,
    height: providedHeight || defaultHeight
  });

  useEffect(() => {
    if (providedWidth && providedHeight) {
      setDimensions({ width: providedWidth, height: providedHeight });
      return;
    }

    const updateDimensions = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setDimensions({
          width: providedWidth || Math.max(300, rect.width - widthOffset),
          height: providedHeight || Math.max(150, rect.height - heightOffset)
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Create ResizeObserver for more efficient resize detection
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [ref, providedWidth, providedHeight, widthOffset, heightOffset]);

  return dimensions;
}